import { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { AlertTriangle, Download, FileCheck, Info, Upload } from 'lucide-react';
import {
  deleteDemoImportJob,
  downloadDemoDataTemplate,
  fetchActiveNodePack,
  importDemoData,
  listDemoImportJobs,
  validateDemoData
} from '../../api';
import { invalidateNodeCatalog } from '../../hooks/useNodeCatalog';
import { useToast } from '../../hooks/use-toast';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const JOB_POLL_ACTIVE_MS = 3000;
const JOB_POLL_IDLE_MS = 15000;

const JOB_STATUS_LABELS = {
  queued: 'В очереди',
  running: 'Выполняется',
  completed: 'Завершена',
  failed: 'Ошибка',
  cancelled: 'Отменена'
};

const formatDate = (value, pattern = 'dd.MM.yyyy HH:mm') => (value ? format(new Date(value), pattern) : '—');

const errorMessage = (error) => error?.response?.data?.message || error.message;

const Stat = ({ label, value, hint }) => (
  <div className="rounded-md border p-3">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-lg font-semibold">{value}</div>
    {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
  </div>
);

const IssuesTable = ({ issues }) => {
  if (!issues?.length) {
    return (
      <p className="flex items-center gap-2 text-sm text-emerald-700">
        <FileCheck className="h-4 w-4" />
        Замечаний нет.
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-28">Уровень</TableHead>
          <TableHead>Замечание</TableHead>
          <TableHead className="w-20 text-right">Строк</TableHead>
          <TableHead className="w-40">Например, строки</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {issues.map((issue) => (
          <TableRow key={`${issue.severity}-${issue.code}-${issue.message}`}>
            <TableCell>
              <Badge variant={issue.severity === 'error' ? 'destructive' : 'secondary'}>
                {issue.severity === 'error' ? 'Ошибка' : 'Предупреждение'}
              </Badge>
            </TableCell>
            <TableCell>
              <div className="font-mono text-xs text-muted-foreground">{issue.code}</div>
              <div className="text-sm">{issue.message}</div>
            </TableCell>
            <TableCell className="text-right">{issue.count}</TableCell>
            <TableCell className="text-xs text-muted-foreground">{issue.sampleLines.join(', ')}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const DemoDataTab = () => {
  const { toast } = useToast();

  const [activePack, setActivePack] = useState(null);
  const [isPackLoaded, setIsPackLoaded] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [report, setReport] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [jobs, setJobs] = useState([]);

  const loadJobs = useCallback(async () => {
    try {
      const list = await listDemoImportJobs();
      setJobs(Array.isArray(list) ? list : []);
    } catch {
      // Вспомогательная панель: без тостов на каждый опрос.
    }
  }, []);

  useEffect(() => {
    fetchActiveNodePack()
      .then(setActivePack)
      .catch(() => setActivePack(null))
      .finally(() => setIsPackLoaded(true));
    loadJobs();
  }, [loadJobs]);

  const hasActiveJob = jobs.some((job) => job.status === 'queued' || job.status === 'running');

  useEffect(() => {
    const interval = setInterval(loadJobs, hasActiveJob ? JOB_POLL_ACTIVE_MS : JOB_POLL_IDLE_MS);
    return () => clearInterval(interval);
  }, [loadJobs, hasActiveJob]);

  // Как только загрузка закончилась, каталог дашборда мог устареть — сбрасываем кэш.
  const completedJobIds = jobs.filter((job) => job.status === 'completed').map((job) => job.id).join(',');
  useEffect(() => {
    if (completedJobIds) {
      invalidateNodeCatalog();
    }
  }, [completedJobIds]);

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      await downloadDemoDataTemplate();
    } catch (error) {
      toast({ title: 'Не удалось скачать шаблон', description: errorMessage(error), variant: 'destructive' });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files?.[0] ?? null);
    // Отчёт относится к конкретному файлу: при смене файла его показывать нельзя.
    setReport(null);
  };

  const handleValidate = async () => {
    if (!selectedFile) {
      return;
    }

    setIsValidating(true);
    try {
      const result = await validateDemoData(selectedFile);
      setReport(result);
      toast({
        title: result.errorCount > 0 ? 'В файле есть ошибки' : 'Файл проверен',
        description: `Звонков: ${result.callCount}. Ошибок: ${result.errorCount}. Предупреждений: ${result.warningCount}.`,
        variant: result.errorCount > 0 ? 'destructive' : undefined
      });
    } catch (error) {
      toast({ title: 'Не удалось проверить файл', description: errorMessage(error), variant: 'destructive' });
    } finally {
      setIsValidating(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !report) {
      return;
    }

    setIsImporting(true);
    try {
      await importDemoData(selectedFile);
      toast({ title: 'Загрузка запущена', description: `Звонков: ${report.callCount}.` });
      setSelectedFile(null);
      setReport(null);
      setFileInputKey((value) => value + 1);
      await loadJobs();
    } catch (error) {
      toast({ title: 'Не удалось запустить загрузку', description: errorMessage(error), variant: 'destructive' });
    } finally {
      setIsImporting(false);
    }
  };

  const canImport = Boolean(report) && report.errorCount === 0 && report.callCount > 0 && !hasActiveJob && !isImporting;

  return (
    <div className="space-y-4">
      {/* ── Шаблон ─────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="flex flex-col gap-3 space-y-0 md:flex-row md:items-start md:justify-between md:gap-0">
          <div>
            <CardTitle>Шаблон файла</CardTitle>
            <CardDescription>
              ZIP с JSON-схемой строки, примером и памяткой. Схема строится по активному пакету узлов:
              в ней перечислены узлы, допустимые значения и пункты чек-листов.
            </CardDescription>
          </div>
          <Button data-ev="settings.demo_data.template_download" variant="outline" onClick={handleDownloadTemplate} disabled={isDownloading}>
            <Download className="mr-2 h-4 w-4" />
            {isDownloading ? 'Готовим…' : 'Скачать шаблон'}
          </Button>
        </CardHeader>
        <CardContent>
          {isPackLoaded && activePack && (
            <p className="text-sm text-muted-foreground">
              Шаблон будет сгенерирован для пакета «{activePack.name}», версия {activePack.version}.
            </p>
          )}
          {isPackLoaded && !activePack && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Активного пакета узлов нет — шаблон будет содержать только общую часть строки, а загрузка
                данных недоступна. Активируйте пакет в «Узлы анализа».
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Загрузка ───────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Загрузка демо-данных</CardTitle>
          <CardDescription>
            Файл JSONL: одна строка — один звонок с разметкой узлов. Звонки, сотрудники, клиенты и сводки
            звонков появятся в аналитике так, будто их разобрал пайплайн.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-full flex-1 space-y-2 sm:min-w-[280px]">
              <Label htmlFor="demo-data-file">Файл данных</Label>
              <Input
                key={fileInputKey}
                id="demo-data-file"
                data-ev="settings.demo_data.file_select"
                type="file"
                accept=".jsonl,.json,.txt,application/json"
                onChange={handleFileChange}
              />
            </div>
            <Button data-ev="settings.demo_data.validate" variant="outline" onClick={handleValidate} disabled={!selectedFile || isValidating || !activePack}>
              <FileCheck className="mr-2 h-4 w-4" />
              {isValidating ? 'Проверяем…' : 'Проверить'}
            </Button>
            <Button data-ev="settings.demo_data.import" onClick={handleImport} disabled={!canImport}>
              <Upload className="mr-2 h-4 w-4" />
              {isImporting ? 'Запускаем…' : 'Загрузить'}
            </Button>
          </div>

          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Данные только дописываются: повторная загрузка того же файла перезаписывает звонки с теми же именами
              файлов, а не дублирует их. У демо-звонков нет аудио и артефактов анализа.
            </span>
          </div>

          {report && (
            <div className="space-y-4 rounded-md border p-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="break-all font-medium">{report.fileName}</span>
                <Badge variant="secondary">Пакет v{report.packVersion}</Badge>
                <span className="text-muted-foreground">
                  {formatDate(report.dateFrom, 'dd.MM.yyyy')} — {formatDate(report.dateTo, 'dd.MM.yyyy')}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Stat
                  label="Звонков"
                  value={report.callCount}
                  hint={`строк: ${report.totalLines}`}
                />
                <Stat
                  label="Входящие / исходящие"
                  value={`${report.incomingCalls} / ${report.outgoingCalls}`}
                  hint={report.missedCalls > 0 ? `пропущенных: ${report.missedCalls}` : null}
                />
                <Stat label="Сотрудники" value={report.employees} hint={`новых: ${report.newEmployees}`} />
                <Stat label="Клиенты" value={report.clients} hint={`новых: ${report.newClients}`} />
              </div>

              {report.coverage?.length > 0 && (
                <div className="space-y-2">
                  <Label>Покрытие узлов</Label>
                  <div className="flex flex-wrap gap-1">
                    {report.coverage.map((node) => (
                      <Badge
                        key={node.nodeId}
                        variant={node.completed > 0 ? 'secondary' : 'outline'}
                        className="font-normal"
                      >
                        {node.nodeId}: {node.completed} из {node.completed + node.skipped}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {report.notices?.length > 0 && (
                <ul className="space-y-1 text-sm text-amber-900">
                  {report.notices.map((notice) => (
                    <li key={notice} className="flex items-start gap-2">
                      <Info className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{notice}</span>
                    </li>
                  ))}
                </ul>
              )}

              <IssuesTable issues={report.issues} />
            </div>
          )}

          {jobs.length > 0 && (
            <div className="space-y-2">
              <Label>Загрузки</Label>
              {jobs.map((job) => (
                <div key={job.id} className="rounded-md border p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span className="min-w-0 [overflow-wrap:anywhere]">
                      {JOB_STATUS_LABELS[job.status] || job.status} · {job.fileName}
                    </span>
                    <span className="text-muted-foreground">
                      {job.processedRows} / {job.totalRows}
                      {job.failedRows > 0 && ` · с ошибкой ${job.failedRows}`}
                    </span>
                  </div>
                  <Progress value={job.progress} />
                  {job.status === 'completed' && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Создано сотрудников: {job.employeesCreated ?? 0}, клиентов: {job.clientsCreated ?? 0}.
                    </p>
                  )}
                  {job.error && <p className="mt-2 text-xs text-red-600">{job.error}</p>}
                  {(job.status === 'completed' || job.status === 'failed') && (
                    <Button
                      data-ev="settings.demo_data.job_dismiss"
                      variant="ghost"
                      size="sm"
                      className="mt-2"
                      onClick={async () => {
                        await deleteDemoImportJob(job.id);
                        await loadJobs();
                      }}
                    >
                      Убрать
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DemoDataTab;
